import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const ROLES = [
  { icon: 'ri-music-2-line', label: 'Musicians' },
  { icon: 'ri-mic-line', label: 'Vocalists' },
  { icon: 'ri-headphone-line', label: 'Audio Engineers' },
  { icon: 'ri-video-line', label: 'Media Teams' },
  { icon: 'ri-broadcast-line', label: 'Live Stream' },
  { icon: 'ri-user-star-line', label: 'Worship Leaders' },
];

export default function MinistryConnectionsBanner() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section className="py-16 bg-white">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8">
        <div
          ref={ref}
          className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} relative rounded-3xl overflow-hidden`}
        >
          {/* Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1C1917] via-[#292524] to-[#1C1917]" />
          <div
            className="absolute inset-0 opacity-[0.07]"
            style={{ backgroundImage: 'radial-gradient(circle at 15% 50%, rgba(234,179,8,0.8) 0%, transparent 55%), radial-gradient(circle at 85% 20%, rgba(234,179,8,0.4) 0%, transparent 45%)' }}
          />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-0">

            {/* ── Left: Image ── */}
            <div className="relative min-h-[320px] lg:min-h-0 overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/9369c9895b7ff62514bcb2a0c5233625.jpg"
                alt="Ministry worship team performing"
                className="w-full h-full object-cover object-top absolute inset-0"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#1C1917]/80" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/60 via-transparent to-transparent" />

              {/* Free badge */}
              <div className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 rounded-full bg-brand-yellow text-[#1C1917] font-body text-xs font-black uppercase tracking-widest shadow-lg">
                <i className="ri-gift-line text-sm" />
                100% Free Service
              </div>
            </div>

            {/* ── Right: Content ── */}
            <div className="px-10 py-12 flex flex-col justify-center">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-yellow/15 border border-brand-yellow/30 text-brand-yellow font-body text-xs font-bold uppercase tracking-widest w-fit mb-6">
                <i className="ri-links-line text-sm" />
                Ministry Staffing &amp; Creative Connections
              </div>

              <h2 className="font-heading text-white text-4xl lg:text-5xl font-black leading-tight mb-4">
                Serving or Seeking?<br />
                <span className="text-brand-yellow">We Facilitate</span><br />
                the Connection.
              </h2>

              <p className="font-body text-white/65 text-sm leading-relaxed mb-7 max-w-sm">
                A free service by Creative Institute LLC — connecting creatives and ministry professionals with churches and organizations that need them. Done for the glory of God.
              </p>

              {/* Role chips */}
              <div className="flex flex-wrap gap-2 mb-8">
                {ROLES.map((role) => (
                  <div
                    key={role.label}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/8 border border-white/15"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${role.icon} text-brand-yellow text-xs`} />
                    </div>
                    <span className="font-body text-white/80 text-xs font-medium">{role.label}</span>
                  </div>
                ))}
              </div>

              {/* Two CTA cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7">
                <Link
                  to="/ministry-connections?tab=creative"
                  className="group flex flex-col gap-1.5 p-4 rounded-2xl bg-white/8 border border-white/15 hover:bg-brand-yellow/10 hover:border-brand-yellow/40 transition-all cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-yellow/20 text-brand-yellow">
                    <i className="ri-user-heart-line text-base" />
                  </div>
                  <p className="font-body text-white font-bold text-sm leading-snug">I&apos;m a Creative</p>
                  <p className="font-body text-white/50 text-xs leading-snug">Looking for ministry opportunities</p>
                  <span className="font-body text-brand-yellow text-xs font-semibold flex items-center gap-1 mt-1 group-hover:gap-2 transition-all">
                    Submit Info <i className="ri-arrow-right-line" />
                  </span>
                </Link>

                <Link
                  to="/ministry-connections?tab=church"
                  className="group flex flex-col gap-1.5 p-4 rounded-2xl bg-white/8 border border-white/15 hover:bg-brand-yellow/10 hover:border-brand-yellow/40 transition-all cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-yellow/20 text-brand-yellow">
                    <i className="ri-community-line text-base" />
                  </div>
                  <p className="font-body text-white font-bold text-sm leading-snug">I&apos;m a Church</p>
                  <p className="font-body text-white/50 text-xs leading-snug">Looking to fill a role or position</p>
                  <span className="font-body text-brand-yellow text-xs font-semibold flex items-center gap-1 mt-1 group-hover:gap-2 transition-all">
                    Submit Request <i className="ri-arrow-right-line" />
                  </span>
                </Link>
              </div>

              <Link
                to="/ministry-connections"
                className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-yellow text-[#1C1917] font-body font-bold text-sm cursor-pointer hover:bg-yellow-300 transition-colors w-fit"
              >
                <i className="ri-links-line text-base" />
                Learn More About This Free Service
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
